import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../common/Button';

const InputContainer = styled(motion.div)`
  background: linear-gradient(145deg, #1E1E1E 0%, #2A2A2A 100%);
  border-radius: 16px;
  padding: 40px;
  margin: 40px 0;
  border: 1px solid #333;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 24px;
    margin: 24px 0;
  }
`;

const InputTitle = styled.h2`
  font-size: 24px;
  color: #FFFFFF;
  margin-bottom: 16px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const InputDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;
  font-size: 16px;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const Form = styled.form`
  display: flex;
  gap: 16px;
  max-width: 500px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const VinInputField = styled.input`
  flex: 1;
  padding: 16px 20px;
  background-color: #121212;
  border: 1px solid #444;
  border-radius: 12px;
  color: #FFFFFF;
  font-size: 16px;
  font-family: 'Roboto Mono', monospace;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 1px #2196F3;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
    text-transform: none;
    letter-spacing: normal;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 14px;
  }
`;

const ErrorMessage = styled(motion.div)`
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid #F44336;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  color: #F44336;
  font-size: 14px;
  text-align: center;
`;

const ExampleVin = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 8px;
  
  p {
    margin: 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    
    strong {
      color: #2196F3;
      font-family: 'Roboto Mono', monospace;
      letter-spacing: 1px;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #333;
  border-top: 2px solid #2196F3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const VinInput = ({ onSubmit, loading, error }) => {
  const [vin, setVin] = useState('');

  const validateVin = (vinNumber) => {
    // Basic VIN validation
    const cleanVin = vinNumber.replace(/[^A-HJ-NPR-Z0-9]/gi, '').toUpperCase();
    return cleanVin.length === 17;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanVin = vin.replace(/[^A-HJ-NPR-Z0-9]/gi, '').toUpperCase();
    
    if (!validateVin(cleanVin)) {
      return;
    }
    
    onSubmit(cleanVin);
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/[^A-HJ-NPR-Z0-9]/gi, '').toUpperCase();
    if (value.length <= 17) {
      setVin(value);
    }
  };

  const isValidVin = validateVin(vin);

  return (
    <InputContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <InputTitle>Enter Vehicle VIN Number</InputTitle>
      <InputDescription>
        Get instant vehicle analysis including reliability scores, potential issues, 
        depreciation forecasts, and a comprehensive buying checklist
      </InputDescription>
      
      <Form onSubmit={handleSubmit}>
        <VinInputField
          type="text"
          value={vin}
          onChange={handleInputChange}
          placeholder="Enter 17-digit VIN"
          maxLength={17}
          disabled={loading}
        />
        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={!isValidVin || loading}
          style={{ minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          {loading ? (
            <>
              <LoadingSpinner />
              Analyzing...
            </>
          ) : (
            'Check Vehicle'
          )}
        </Button>
      </Form>

      {error && (
        <ErrorMessage
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          {error}
        </ErrorMessage>
      )}

      <ExampleVin>
        <p>
          <strong>Example VIN:</strong> 1HGCV1F35MA090575 (2021 Honda Accord)
        </p>
      </ExampleVin>
    </InputContainer>
  );
};

export default VinInput;